import React, { useEffect, useState } from "react";
import { Box, Pagination, Typography, Skeleton, Fade } from "@mui/material";
import { vendorApi } from "../../api/api";
import EnquiryCard from "../vendorenquiries/EnquiryCard";
import Loader from "../common/Loader";

/* ─── inject fonts once ─── */
const injectFonts = () => {
  if (document.getElementById("eq-fonts")) return;
  const link = document.createElement("link");
  link.id = "eq-fonts";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Nunito:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);
};

/* ─── inline styles ─── */
const S = {
  wrapper: {
    minHeight: "100vh",
    fontFamily: "'Nunito', sans-serif",
    p: { xs: 2, sm: 3, md: 4 },
  },
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 2,
    mb: 4,
    pb: 3,
    borderBottom: "1px solid rgba(0,0,0,0.07)",
  },
  titleBlock: { display: "flex", flexDirection: "column", gap: 0.5 },
  eyebrow: {
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "#1565c0",
    fontFamily: "'Nunito', sans-serif",
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: { xs: "1.6rem", sm: "2rem" },
    fontWeight: 700,
    color: "#1a1208",
    lineHeight: 1.15,
    m: 0,
  },
 badge: (n) => ({
  display: "inline-flex",
  alignItems: "center",
  background: n > 0 ? "#1565c0" : "#d1d5db",
  color: "#fff",
  borderRadius: "999px",
  px: 1.8,
  py: 0.4,
  fontSize: "0.78rem",
  fontWeight: 700,
}),
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    py: 10,
    gap: 2,
  },
  emptyIcon: {
  width: 72,
  height: 72,
  borderRadius: "50%",
  background: "rgba(21,101,192,0.12)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "2rem",
},
  listWrap: {
    display: "grid",
    gap: 2, // gap between cards
    gridTemplateColumns: "repeat(1, 1fr)", // default mobile
    "@media (min-width:600px)": {  // small tablets
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    "@media (min-width:900px)": { // tablets
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    "@media (min-width:1200px)": { // desktop
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  },
  cardShell: (i) => ({
    animation: `eqFadeUp 0.4s ease both`,
    animationDelay: `${i * 0.07}s`,
    "@keyframes eqFadeUp": {
      from: { opacity: 0, transform: "translateY(14px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  }),
  skeletonCard: {
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.06)",
    borderRadius: "16px",
    p: 2.5,
    display: "flex",
    gap: 2,
    alignItems: "flex-start",
  },
  pageWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 2,
    mt: 5,
    pt: 3,
    borderTop: "1px solid rgba(0,0,0,0.07)",
  },
  pageInfo: {
    fontSize: "0.82rem",
    color: "#9e8c78",
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 500,
  },
};

/* ─── skeleton placeholder ─── */
const CardSkeleton = () => (
  <Box sx={S.skeletonCard}>
    <Skeleton variant="circular" width={44} height={44} sx={{ flexShrink: 0, bgcolor: "#f0ebe3" }} />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="55%" height={20} sx={{ bgcolor: "#f0ebe3", mb: 1 }} />
      <Skeleton variant="text" width="35%" height={16} sx={{ bgcolor: "#f0ebe3", mb: 1.5 }} />
      <Skeleton variant="text" width="80%" height={14} sx={{ bgcolor: "#f0ebe3" }} />
      <Skeleton variant="text" width="65%" height={14} sx={{ bgcolor: "#f0ebe3", mt: 0.5 }} />
    </Box>
    <Skeleton variant="rounded" width={76} height={26} sx={{ bgcolor: "#f0ebe3", borderRadius: "999px" }} />
  </Box>
);

/* ─── pagination override styles injected once ─── */
const injectPaginationCSS = () => {
  if (document.getElementById("eq-pagination")) return;
  const style = document.createElement("style");
  style.id = "eq-pagination";
  style.textContent = `
    .eq-pagination .MuiPaginationItem-root {
      font-family: 'Nunito', sans-serif;
      font-weight: 600;
      font-size: 0.82rem;
      border-radius: 10px;
      border: 1px solid #e8ddd0;
      color: #7a6a58;
      background: #fff;
      min-width: 36px;
      height: 36px;
      transition: all 0.18s ease;
    }
    .eq-pagination .MuiPaginationItem-root:hover {
  background: #eff6ff;
  border-color: #1565c0;
  color: #1565c0;
}

.eq-pagination .MuiPaginationItem-root.Mui-selected {
  background: #1565c0 !important;
  border-color: #1565c0 !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(21,101,192,0.35);
}
    .eq-pagination .MuiPaginationItem-ellipsis {
      border: none; background: transparent;
    }
  `;
  document.head.appendChild(style);
};

/* ════════════════════════════════ COMPONENT ════════════════════════════════ */
const EnquiryList = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => { injectFonts(); injectPaginationCSS(); }, []);

  const fetchEnquiries = async (pageNo = 1) => {
    try {
      setLoading(true);
      const res = await vendorApi.getVendorEnquiries(pageNo);
      setEnquiries(res.data.data.data || []);
      setLastPage(res.data.data.last_page || 1);
      setTotal(res.data.data.total || 0);
    } catch (err) {
      console.error("Failed to load enquiries", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEnquiries(page); }, [page]);

  const from = (page - 1) * (total / lastPage | 0) + 1;
  const to = Math.min(page * (total / lastPage | 0), total);

  return (
    <Box sx={S.wrapper}>

      {/* ── Header ── */}
      <Box sx={S.header}>
        <Box sx={S.titleBlock}>
          <Typography sx={S.eyebrow}>Vendor Portal</Typography>
          <Typography component="h1" sx={S.title}>Customer Enquiries</Typography>
        </Box>
        {!loading && (
          <Fade in>
            <Box sx={S.badge(enquiries.length)}>
              {total > 0 ? `${total} total` : "0 enquiries"}
            </Box>
          </Fade>
        )}
      </Box>

      {/* ── Skeletons while loading ── */}
      {loading && (
        <Box sx={S.listWrap}>
          {[...Array(4)].map((_, i) => <CardSkeleton key={i} />)}
        </Box>
      )}

      {/* ── Empty state ── */}
      {!loading && enquiries.length === 0 && (
        <Box sx={S.emptyState}>
          <Box sx={S.emptyIcon}>📭</Box>
          <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.15rem", color: "#4a3f32", fontWeight: 600 }}>
            No enquiries yet
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#9e8c78", textAlign: "center", maxWidth: 300 }}>
            When customers enquire about your properties, they'll show up here.
          </Typography>
        </Box>
      )}

      {/* ── Cards ── */}
      {!loading && enquiries.length > 0 && (
        <Box sx={S.listWrap}>
          {enquiries.map((item, i) => (
            <Box key={item.EnquiryID} sx={S.cardShell(i)}>
              <EnquiryCard enquiry={item} />
            </Box>
          ))}
        </Box>
      )}

      {/* ── Pagination ── */}
      {!loading && lastPage > 1 && (
        <Box sx={S.pageWrap}>
          <Typography sx={S.pageInfo}>
            Showing <strong style={{ color: "#4a3f32" }}>{from}–{to}</strong> of <strong style={{ color: "#4a3f32" }}>{total}</strong> enquiries
          </Typography>
          <Pagination
            count={lastPage}
            page={page}
            onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            shape="rounded"
            siblingCount={1}
            className="eq-pagination"
          />
        </Box>
      )}

    </Box>
  );
};

export default EnquiryList;